/**
 * PushDrawer — shared right-side drawer shell for Push-Mode work.
 *
 * Why a drawer (vs centered dialog)?
 *   • Push Mode's whole spirit: "you haven't left your post." A right-side
 *     drawer keeps the underlying dashboard visible, reinforcing context
 *     instead of interrupting it.
 *   • Multi-section content (urgency banner + brief + progress + scheduler
 *     + form + footer) breathes. Full viewport height, one stable scroll
 *     lane, sticky header & footer — no scroll trap.
 *   • Spatial consistency with Build Review (IntakeReviewWorkspace) trains
 *     operators on one motion vocabulary: things slide in from the right
 *     when there's deep work to do.
 *
 * Layout:
 *   ┌─ Sticky header (title row, badge, close) ─┐
 *   │                                            │
 *   │  Scrollable body (the work)                │
 *   │                                            │
 *   ├─ Sticky footer (Cancel / Confirm) ─────────┤
 *   └────────────────────────────────────────────┘
 *
 * Tier guidance (when to use what):
 *   • PushDrawer       → multi-section work (Resolve, Reschedule, Assign, Ordering, Permit)
 *   • Centered Dialog  → confirmations, destructive guards, one-question prompts
 *   • Toast / inline   → ack-only feedback
 */
import * as React from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PushDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sticky header content — typically a title + meta row. Required for a11y title. */
  header: React.ReactNode;
  /** Sticky footer content — typically Cancel / Confirm buttons. Optional. */
  footer?: React.ReactNode;
  /** Scrollable body content. */
  children: React.ReactNode;
  /** Tailwind max-width override. Default: max-w-2xl (672px) — matches prior modal. */
  widthClass?: string;
  /** Optional class on the body wrapper for spacing tweaks. */
  bodyClassName?: string;
  /** When true, hide the built-in close button (caller renders its own). */
  hideClose?: boolean;
}

/**
 * Drop-in shell. Caller owns header / footer content; shell owns the
 * sliding motion, sticky chrome, and scroll behavior.
 */
export function PushDrawer({
  open,
  onOpenChange,
  header,
  footer,
  children,
  widthClass = "sm:max-w-2xl",
  bodyClassName,
  hideClose = false,
}: PushDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "w-full p-0 gap-0 flex flex-col [&>button]:hidden",
          widthClass,
        )}
      >
        {/* Sticky header */}
        <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shrink-0">
          <div className="flex-1 min-w-0">{header}</div>
          {!hideClose && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 shrink-0 -mr-1 -mt-0.5"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Scrollable body */}
        <div className={cn("flex-1 overflow-y-auto px-5 py-4", bodyClassName)}>
          {children}
        </div>

        {/* Sticky footer */}
        {footer && (
          <div className="border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 px-5 py-3 shrink-0">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
